import { RefObject, useLayoutEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { calculateSelectedAreaFocusAnchor } from '@ironview/interactive-viewer';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/utils/utils';
import { Position, Unit } from '../types/types';

interface SelectedAreaFocusControlProps {
  unit: Unit | null;
  containerRef: RefObject<HTMLDivElement>;
  svgRef: RefObject<SVGSVGElement>;
  position: Position;
  scale: number;
  isPdfLayout: boolean;
  onBackToOverview?: () => void;
}

export const SelectedAreaFocusControl = ({
  unit,
  containerRef,
  svgRef,
  position,
  scale,
  isPdfLayout,
  onBackToOverview,
}: SelectedAreaFocusControlProps) => {
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const areaName = unit ? Object.keys(unit.images || {})[0] || `Area ${unit.id}` : '';

  useLayoutEffect(() => {
    const updateAnchor = () => {
      const container = containerRef.current;
      const svg = svgRef.current;
      if (!unit || !container || !svg) {
        setAnchor(null);
        return;
      }

      const svgRect = svg.getBoundingClientRect();
      const viewBox = svg.viewBox.baseVal;
      const containerRect = container.getBoundingClientRect();

      setAnchor(
        calculateSelectedAreaFocusAnchor({
          points: unit.points.map(([x, y]) => [x, y] as const),
          position,
          scale,
          svgRect,
          viewBox,
          containerRect,
          svgRectIncludesViewportTransform: isPdfLayout,
        })
      );
    };

    updateAnchor();

    const frame = window.requestAnimationFrame(updateAnchor);
    const resizeObserver = new ResizeObserver(updateAnchor);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', updateAnchor);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateAnchor);
    };
  }, [containerRef, isPdfLayout, position.x, position.y, scale, svgRef, unit]);

  if (!unit || !anchor) return null;

  return (
    <div
      className="selected-area-focus-control pointer-events-auto absolute z-40 flex max-w-[min(22rem,calc(100%-2rem))] -translate-x-1/2 -translate-y-full items-center gap-2 rounded-[12px] border border-border bg-card/95 p-1.5 text-foreground shadow-slate ring-1 ring-foreground/5 backdrop-blur-md"
      style={{ left: anchor.x, top: anchor.y }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
    >
      <Button
        type="button"
        variant="default"
        size="sm"
        className="h-8 rounded-[9px] px-2.5 text-xs"
        onClick={onBackToOverview}
      >
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Overview
      </Button>
      <div className="h-6 w-px shrink-0 bg-border" />
      <Label
        title={areaName}
        className={cn(
          'min-w-0 max-w-44 truncate px-1.5 text-sm font-semibold text-foreground',
          'leading-8'
        )}
      >
        {areaName}
      </Label>
    </div>
  );
};
